import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as SelectPrimitive from '@radix-ui/react-select';

import { Select, SelectContent, SelectItem } from 'src/component/ds/ui/select';
import {
  SegmentedToggle, segmentedToggleItemVariants, type SegmentedToggleOption
} from 'src/component/ds/ui/segmentedToggle';
import { cn } from 'src/component/ds/utils';
import { fetchUnits, setSelectedUnit } from 'src/module/filter/action';
import { getClientId } from 'src/module/authentication/selector';
import { getSelectedUnit, getUnitNames } from 'src/module/filter/selector';
import useBreakpoint from 'src/hooks/useBreakpoint';
import { useTranslation } from 'src/i18n';

const MENU_CLASSES = 'ds-max-h-[320px]';

/** The app-wide "no filter" sentinel. */
const NO_AREA = '';

/*
 * How many areas the row holds before it has been measured, per breakpoint.
 */
const AREA_COUNT = { phone: 1, mobile: 5, desktop: 5 };

const GUTTER = 16;

/*
 * How many areas fit in `available` px. `widths` is All Areas, each area, then
 * the trigger. When they don't all fit, room for the trigger and the selection
 * is set aside first.
 */
export function areasThatFit ([all, ...rest]: number[], available: number, selectedIndex: number) {
  const more = rest.pop() as number;
  if (rest.reduce((sum, width) => sum + width, all) <= available) return rest.length;

  let used = all + more + (rest[selectedIndex] ?? 0);
  let count = 0;
  while (count < rest.length && used + rest[count] <= available) used += rest[count++];
  return count;
}

/**
 * Works out how many areas the row can show at its current width, so the
 * rest can go behind the "+N more" menu. Re-fits when the row resizes.
 *
 * @param {string[]} areas every area name, in row order
 * @param {string} selected the selected area, or '' for All Areas
 * @returns {{ containerRef, measureRef, fitted }} `containerRef` goes on the
 *   filter's outer element, whose width is the space available. `measureRef`
 *   goes on the hidden row of every segment at natural width, in the order
 *   All Areas, each area, then the trigger. `fitted` is the number of areas to
 *   show inline, or null until measured (and where there's no layout, e.g.
 *   jsdom), so the caller falls back to a fixed count.
 */
function useAreasThatFit (areas: string[], selected: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fitted, setFitted] = useState<number | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || typeof ResizeObserver === 'undefined') return undefined;

    let widths: number[] = [];
    const observer = new ResizeObserver((entries) => {
      if (entries.some((entry) => entry.target === measure)) {
        widths = Array.from(measure.children, (child) => child.getBoundingClientRect().width);
      }
      const isHidden = container.clientWidth === 0;
      if (isHidden) return;
      // An unchanged count is the same state, so React skips the re-render.
      setFitted(areasThatFit(widths, container.clientWidth - GUTTER, areas.indexOf(selected)));
    });
    observer.observe(container);
    observer.observe(measure);

    return () => observer.disconnect();
  }, [areas, selected]);

  return { containerRef, measureRef, fitted };
}

/*
 * An invisible copy of every segment at its natural width, never seen by the
 * user. useAreasThatFit reads each child's width to decide how many real
 * segments fit. The outer div clips it to the filter so it can't widen the page.
 */
const MeasuringRow = React.forwardRef<HTMLDivElement, { labels: string[] }>(({ labels }, ref) => (
  <div className='ds-invisible ds-pointer-events-none ds-absolute ds-inset-0 ds-overflow-hidden' aria-hidden>
    <div ref={ref} className='ds-absolute ds-left-0 ds-top-0 ds-flex ds-w-max'>
      {labels.map((label, index) => (
        <span key={index} className={segmentedToggleItemVariants()}><span>{label}</span></span>
      ))}
    </div>
  </div>
));
MeasuringRow.displayName = 'MeasuringRow';

/*
 * "Area" is this screen's name for a unit: the data is devices.unit and the
 * selection rides in the shared filter slice, so this is the same filter the
 * old UI's toggle bar sets. Areas that don't fit go behind a "+N more" menu.
 */
export default function AreaFilter ({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const clientId = useSelector(getClientId);
  const areas = useSelector(getUnitNames);
  const selectedUnit = useSelector(getSelectedUnit);

  useEffect(() => {
    dispatch(fetchUnits());
  }, [dispatch, clientId]);

  /* A stored area the facility no longer has is dropped by the reducer once
   * the units load; until then it shows as appended to the row. */
  const selected = selectedUnit || NO_AREA;

  const { isMobile, isPhone } = useBreakpoint();
  const fallbackLimit = isPhone ? AREA_COUNT.phone : isMobile ? AREA_COUNT.mobile : AREA_COUNT.desktop;

  const moreLabel = t('dashboard.areaFilterMore', { count: areas.length });
  const { containerRef, measureRef, fitted } = useAreasThatFit(areas, selected);
  const limit = fitted ?? fallbackLimit;

  /* The row is the first `limit` areas, plus the selection when it's past them
   * so it stays visible; the menu is everything past `limit` either way. */
  const { inline, overflow } = useMemo(() => {
    const head = areas.slice(0, limit);
    const rest = areas.slice(limit);
    const appended = selected && !head.includes(selected) ? [selected] : [];

    return { inline: [...head, ...appended], overflow: rest };
  }, [areas, selected, limit]);

  const options: SegmentedToggleOption[] = useMemo(
    () => [
      { value: NO_AREA, label: t('dashboard.allAreas') },
      ...inline.map((area) => ({ value: area, label: area })),
    ],
    [inline, t]
  );

  const setArea = (area: string) => dispatch(setSelectedUnit(area));

  return (
    <div
      ref={containerRef}
      className={cn('ds-relative ds-flex ds-items-center', className)}
      data-testid='area-filter'
    >
      <MeasuringRow ref={measureRef} labels={[t('dashboard.allAreas'), ...areas, moreLabel]} />

      <SegmentedToggle
        options={options}
        value={selected}
        onChange={setArea}
        aria-label={t('dashboard.areaFilterLabel')}
        // The trigger butts onto the row, so the row drops its right pill end.
        className={cn(overflow.length && 'segmented-toggle--open-end')}
        testId='area-filter-segment'
      />

      {overflow.length
        ? (
          <Select value={selected} onValueChange={setArea}>
            <SelectPrimitive.Trigger
              className={cn(
                segmentedToggleItemVariants({ left: 'inner', right: 'end' }),
                'data-[state=open]:ds-bg-muted'
              )}
              aria-label={t('dashboard.areaFilterMoreLabel', { count: overflow.length })}
              data-testid='area-filter-more'
            >
              {t('dashboard.areaFilterMore', { count: overflow.length })}
            </SelectPrimitive.Trigger>
            <SelectContent align='start' className={MENU_CLASSES}>
              {overflow.map((area) => (
                <SelectItem key={area} value={area} data-testid={`area-filter-option-${area}`}>
                  {area}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
        : null}
    </div>
  );
}
